import { Paper, Table, Center, Loader, Box } from "@mantine/core";
import { memo, useContext, useRef, useEffect } from "react";
import { AmountDisplay } from "../Components/AmountDisplay";
import { TokenDisplay } from "../Components/TokenDisplay";
import { LoadingButton } from "../Components/LoadingButton";
import { treemapColors } from "../Components/Highcharts";
import { shrink, useIsSmallScreen } from "../Components/Utils";
import { useNavigate } from "../Components/Link";
import { useGlobalStatusContext } from "../GlobalStore";
import { Td, TdText, Th, ThText } from "../Components/EpTable";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import HighchartsTreemap from "highcharts/modules/treemap";
import { EpPageContext } from "../Components/EpPage";
import { useFireStoreQuery } from "../Components/useFireStoreQuery";
import { FromNow } from "../Components/FromNow";

HighchartsTreemap(Highcharts);

const PAGE_SIZE = 15;

export let HotTableHeight = 0;
export type HotTokenDisplayStyle = "table" | "chart";
export const HotTokenList = memo(function ({
  lite,
  chain,
  sortBy,
  displayStyle = "table",
  hideAddress,
  height,
}: {
  lite?: boolean;
  chain: string;
  sortBy?: string;
  displayStyle?: HotTokenDisplayStyle;
  hideAddress?: boolean;
  height?: number;
}) {
  const { period, navExpand } = useGlobalStatusContext();
  const isSmallScreen = useIsSmallScreen();

  const {
    data,
    isLoading,
    isLoadingMoreData: isFetchingNextPage,
    loadMoreData: fetchNextPage,
    noMoreData,
  } = useFireStoreQuery<HotTokenRoot[]>({
    path: `/${chain}/${period}d/home_hot_token/${
      sortBy === "vroc" ? "volumeChangeRate" : "volume"
    }`,
    orderBy: {
      fieldName: "tradingVolume",
      direction: "desc",
    },
    limit: PAGE_SIZE,
  });

  const { data: root, isLoading: hotLoading } = useFireStoreQuery<any>({
    path: `/${chain}/${period}d/home_hot_token`,
  });

  const table = useRef<HTMLTableElement>(null);
  const navigate = useNavigate();
  const helmetCtx = useContext(EpPageContext);

  const chartComponentRef = useRef<HighchartsReact.RefObject>(null);

  useEffect(() => {
    chartComponentRef.current?.chart.reflow();
  }, [navExpand]);

  if (!data && isLoading) {
    return (
      <Center>
        <Loader className="mantine-loader" />
      </Center>
    );
  }

  const tokens: Array<string> = [];
  const series: Array<any> = [];

  let idx = 0;
  (data ?? []).forEach((item) => {
    series.push({
      name: item.symbol || shrink(item.address),
      value: sortBy === "vroc" ? item.changeRate : item.tradingVolume,
      address: item.address,
      color: treemapColors && treemapColors[idx++ % treemapColors.length],
    });
    tokens.push(item.symbol);
  });

  const { pageDesc } = helmetCtx.page();
  if (pageDesc) {
    helmetCtx.setPage({
      pageDesc: pageDesc
        .replace("[CHAIN]", chain)
        .replace(
          "[SORT-BY]",
          sortBy === "vroc" ? "trading volume change %" : "trading volume"
        )
        .replace("[HOT-TOKENS]", tokens.join(",")),
    });
  }

  HotTableHeight = table?.current?.clientHeight || HotTableHeight || 0;

  const options: Highcharts.Options = {
    chart: {
      height: height || 640,
    },
    title: {
      style: {
        display: "none",
      },
    },
    series: [
      {
        type: "treemap",
        layoutAlgorithm: "squarified",
        data: series,
      },
    ],
    plotOptions: {
      series: {
        events: {
          click: (e: any) => {
            navigate(`/${chain}/token/${e.point.address}`);
          },
        },
      },
    },
    tooltip: {
      valueDecimals: 2,
      valuePrefix: sortBy === "vroc" ? "" : "$",
      valueSuffix: sortBy === "vroc" ? "%" : "",
    },
  };

  const hotLpTable = (
    <>
      <Paper
        sx={{
          maxWidth: "100%",
          overflow: "auto",
          height: height || "auto",
        }}
      >
        <Table highlightOnHover ref={table} horizontalSpacing={20}>
          {isSmallScreen ? (
            <colgroup>
              <col span={1} style={{ width: "50%" }} />
              <col span={1} style={{ width: "50%" }} />
            </colgroup>
          ) : (
            <colgroup>
              <col span={1} style={{ width: "40%" }} />
              {!hideAddress && !isSmallScreen ? (
                <col span={1} style={{ width: "30%" }} />
              ) : null}
              <col span={1} style={{ width: "30%" }} />
            </colgroup>
          )}
          <thead>
            <tr>
              <Th style={{ textAlign: "left" }}>
                <ThText>Symbol</ThText>
              </Th>
              {!hideAddress && !isSmallScreen ? (
                <Th>
                  <ThText>Token Address</ThText>
                </Th>
              ) : null}
              <Th style={{ textAlign: "right" }}>
                <ThText>Score</ThText>
              </Th>
            </tr>
          </thead>
          <tbody>
            {(data ?? []).map((item) => (
              <tr key={item.address}>
                <Td align="left">
                  <TokenDisplay
                    token={{
                      chain,
                      address: item.address,
                      name: item.symbol,
                    }}
                    labelList={item.labelList}
                    showOpener={false}
                  />
                </Td>
                {!hideAddress && !isSmallScreen ? (
                  <Td>
                    <TdText>
                      <TokenDisplay
                        token={{
                          chain: item.chain.name,
                          address: item.address,
                        }}
                      />
                    </TdText>
                  </Td>
                ) : null}
                <Td align="right">
                  {sortBy === "vroc" ? (
                    <AmountDisplay
                      digits={2}
                      value={item.changeRate}
                      suffix="%"
                    />
                  ) : (
                    <AmountDisplay value={item.tradingVolume} />
                  )}
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Paper>

      {!lite &&
      (data ?? [])?.length > 0 &&
      (data ?? [])?.length % PAGE_SIZE === 0 ? (
        <Box
          sx={(theme) => ({
            padding: "16px 20px 19px",
            borderTop:
              theme.colorScheme === "dark"
                ? `1px solid ${theme.colors.dark[6]}`
                : `1px solid ${theme.colors.gray[2]}`,
          })}
        >
          {noMoreData ? null : (
            <LoadingButton
              lite={lite}
              to={`/${chain}/tokens/hot`}
              fetchNextPage={fetchNextPage}
              loading={isLoading || isFetchingNextPage}
            />
          )}
        </Box>
      ) : null}
    </>
  );
  return (
    <Paper>
      {displayStyle === "table" ? (
        hotLpTable
      ) : (
        <HighchartsReact
          highcharts={Highcharts}
          options={options}
          ref={chartComponentRef}
        />
      )}
      <Box
        sx={(theme) => ({
          padding: "9px 20px",
          borderTop:
            theme.colorScheme === "dark"
              ? `1px solid ${theme.colors.dark[6]}`
              : `1px solid ${theme.colors.gray[2]}`,
        })}
      >
        <FromNow
          isLoading={hotLoading}
          updateTimestamp={root?.updateTimestamp!}
          updateBlockNumber={root?.updateBlockNumber!}
        />
      </Box>
    </Paper>
  );
});

export default HotTokenList;
